import React, {
    FC,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';

import BasicButton from '../../../components/buttons/basicButton';
import ComponentTypesIconWrapper from '../../../components/componentTypesIcon/ComponentTypesIconWrapper';
import AampeTooltip from '../../../components/shared/AampeTooltip/AampeTooltip';
import useVariantsByComponentIdQuery from 'hooks/components/useVariantsByComponentIdQuery';
import styled from 'styled-components';

//constants
import aampeTheme from '../../../constants/aampeTheme';
import ICONS from '../../../constants/icons';
import {
    ToastDisplayType,
    ToastStatus,
} from '../../../constants/toastMessage.constants';
import {
    CLIPBOARD_ACCESS_ERROR,
    CLIPBOARD_ACCESS_ERROR_MESSAGE,
    INSUFFICIENT_ROWS_ERROR_MESSAGE,
} from './constants';

//context
import { EditorContext } from '../../../context/EditorContext';

import { createShouldForwardProp } from 'helpers/shouldForwardProp';

import useComponentTypesQuery from '../../../hooks/component-types/useComponentTypesQuery';
import useGetComponentTypeIdByVariant from '../../../hooks/component-types/useGetComponentTypeIdByVariant';
import useLabelsQuery from '../../../hooks/labels/useLabelsQuery';
import useOutsideClickDetector from '../../../hooks/useOutsideClickDetector';
import useVariantBulkCreateMutation from 'hooks/variants/useVariantCreateBulkMutation';

//models
import { AlternateLabelCopyPasteModel } from '../../../models/alternate.model';
import { ComponentTypeMapped } from '../../../models/component-types-model';
import { AlternateHeaderChildrenType } from '../../../models/formula.model';
import {
    GamificationItemModel,
    UnlabelledLabel,
} from '../../../models/label.model';
import { VariantCreateBulk, VariantWithLabel } from 'models/variant.model';

// services
import { toastService } from '../../../services/toast.service';

import AddAlternateListItem from '../formulaEditor/assistant/alternates/AddAlternateListItem';
//components
import AlternatesListWrapper from '../formulaEditor/assistant/alternates/AlternateListWrapper';
import LabelGamificationTray from '../formulaEditor/assistant/gamification/LabelGamificationTray';

const shouldForwardProps = createShouldForwardProp([
    'bgColor',
    'isBorderRight',
    'hoverBgColor',
    'isPasteButton',
]);
interface StyledProps {
    bgColor?: string;
    isBorderRight?: boolean;
    hoverBgColor?: string;
}
interface FormulaAlternateVariantDataViewProps {
    headerName: string;
    alternateCreateError?: string | null;
    setAlternateCreateError?: (val: string | null) => void;
    formulaId: number;
    isEditable: boolean;
    childData: AlternateHeaderChildrenType;
    selectedElement: { [key: string]: any };
    alternateVariantChangeIcon?: ReactNode;
}
const FormulaAlternateVariantDataView: FC<FormulaAlternateVariantDataViewProps> =
    ({
        headerName,
        setAlternateCreateError,
        alternateCreateError,
        formulaId,
        isEditable,
        childData,
        selectedElement,
        alternateVariantChangeIcon,
    }) => {
        const { data: componentTypes } = useComponentTypesQuery();
        const [showPasteButton, setShowPasteButton] = useState(false);

        const cardRef = useOutsideClickDetector(() => {
            setShowPasteButton(false);
        });

        const { data = [], isPending: isLoading } =
            useVariantsByComponentIdQuery(childData.id);

        const variantsBulkCreateMutation = useVariantBulkCreateMutation(
            formulaId,
            childData.id,
        );

        const variantData = useMemo(
            () => componentTypes?.map[childData.variantType as string],
            [childData, componentTypes],
        );

        const Icon = useMemo(
            () => (
                <ComponentTypesIconWrapper
                    dataKey={(variantData as ComponentTypeMapped)?.data_key}
                />
            ),
            [variantData],
        );

        const bgColor = useMemo<string>(
            () => variantData?.secondary_color ?? '',
            [variantData],
        );

        const hoverBgColor = useMemo<string>(
            () => variantData?.medium_color ?? '',
            [variantData],
        );
        const { editorDetails } = useContext(EditorContext);

        const componentTypeId = useGetComponentTypeIdByVariant(
            selectedElement.variantType as string,
        );

        const { data: labels = [] } = useLabelsQuery(componentTypeId as string);

        const filteredAlternates = useMemo<VariantWithLabel[]>(
            () =>
                editorDetails.selectedLabel
                    ? editorDetails.selectedLabel?.id === UnlabelledLabel.id
                        ? data?.filter(
                              (alternate: VariantWithLabel) => !alternate.label,
                          )
                        : data?.filter(
                              (alternate: VariantWithLabel) =>
                                  alternate.label?.id ===
                                  editorDetails.selectedLabel?.id,
                          )
                    : data,
            [data, editorDetails],
        );

        const labelsWithAlternateData: GamificationItemModel[] = useMemo(
            () =>
                labels
                    .map((label) => ({
                        ...label,
                        count: data.filter(
                            (alternate: any) =>
                                alternate?.label?.id === label.id,
                        ).length,
                    }))
                    .sort((labelA, labelB) => {
                        if (labelA.count === labelB.count) {
                            return labelA.label_name.localeCompare(
                                labelB.label_name,
                            );
                        }
                        return labelB.count - labelA.count;
                    }),
            [data, labels],
        );

        const inProgressGamificationList: GamificationItemModel[] = useMemo(
            () => labelsWithAlternateData.filter((label) => label.count > 0),
            [labelsWithAlternateData],
        );

        const handleVariantHeaderClick = useCallback(() => {
            if (isEditable) {
                setShowPasteButton(true);
            }
        }, [isEditable]);

        const handlePasteClick = async () => {
            try {
                const masterVariantId = childData.id;
                const variantType = childData.variantType;

                // Read text data from the clipboard
                const inputData = await navigator.clipboard.readText();

                // Split the input data into rows based on newline character. Trim whitespace off
                // each row and filter out empty rows.
                const rows = inputData
                    .trim()
                    .split('\n')
                    .map((row) => row.trim())
                    .filter((row) => row !== '');

                // Check if there are less than 3 rows in the input data
                if (rows.length < 3) {
                    // Show error toast message and hide the paste button
                    toastService.showError(INSUFFICIENT_ROWS_ERROR_MESSAGE);
                    setShowPasteButton(false);
                    return;
                }

                // Get the component type from the first row
                const componentType = rows[0].split('\t')?.[0];
                // Check if the variant type does not match the component type
                if (
                    variantType?.toLocaleLowerCase() !==
                    componentType?.replaceAll(' ', '')?.toLocaleLowerCase()
                ) {
                    toastService.showError(
                        `Component type mismatch: Got ${componentType}, expected ${
                            componentTypes?.map[variantType as string]?.name
                        }`,
                    );
                    setShowPasteButton(false);
                    return;
                }

                // Get the header row by splitting the second row
                const headerRow = rows[1].split('\t');
                const alternateIndex = headerRow.indexOf('Alternate');
                // Check if the "Alternates" column is not found
                if (alternateIndex === -1) {
                    toastService.showError(
                        'Error: "Alternate" column not found in the sheet.',
                    );
                    setShowPasteButton(false);
                    return;
                }
                const labelIndex = headerRow.indexOf('Label');

                // Create a set to keep track of encountered alternates so not duplicate gets created
                const encounteredAlternates = new Set();
                // Process the data rows (starting from the third row) to extract alternates and labels
                const alternatesData = rows
                    .slice(2)
                    .reduce((acc: AlternateLabelCopyPasteModel[], row) => {
                        const columns = row.split('\t');
                        const alternate = columns[alternateIndex]?.trim();
                        const label =
                            labelIndex !== -1
                                ? columns[labelIndex]?.trim()
                                : undefined;
                        // Check if the alternate is valid and has not been encountered before
                        if (
                            alternate &&
                            !data.some(
                                (_alternate) =>
                                    _alternate.content === alternate,
                            ) &&
                            !encounteredAlternates.has(alternate)
                        ) {
                            // Find the label ID for the given label name
                            const label_id = labels.find(
                                (_label) => _label.label_name === label,
                            )?.id;
                            acc.push({
                                alternate,
                                label: label_id ? label_id : undefined,
                            });
                            // Mark the alternate as encountered
                            encounteredAlternates.add(alternate);
                        }

                        return acc;
                    }, []);
                // Map the extracted alternates data to the mutation data format
                const mutationData: VariantCreateBulk[] = alternatesData.map(
                    (item: AlternateLabelCopyPasteModel) => ({
                        component_id: masterVariantId,
                        content: item.alternate,
                        ...(item.label ? { label_id: item.label } : {}),
                    }),
                );

                // Perform the mutation to create alternates in bulk
                variantsBulkCreateMutation.mutate(mutationData, {
                    onSuccess: () => {
                        setShowPasteButton(false);
                    },
                });
            } catch (e: any) {
                if (e?.name === CLIPBOARD_ACCESS_ERROR) {
                    toastService.showNotificationToast(
                        ToastStatus.Warning,
                        'Access blocked',
                        ToastDisplayType.Snack,
                        CLIPBOARD_ACCESS_ERROR_MESSAGE,
                    );
                }
            }
        };

        const labelledAlternateLength = useMemo<number>(
            () =>
                data?.filter((alternate: VariantWithLabel) => alternate.label)
                    .length,
            [data],
        );

        return (
            <Container ref={cardRef} isPasteButtonShow={showPasteButton}>
                <>
                    <AampeTooltip
                        title={
                            data?.length !== labelledAlternateLength ? (
                                <ToolTipContainer>
                                    You have {data.length} alternates for{' '}
                                    {headerName},{' '}
                                    {labelledAlternateLength
                                        ? labelledAlternateLength
                                        : 'None'}{' '}
                                    of them are labeled. Please, label all for
                                    agents efficiency.
                                </ToolTipContainer>
                            ) : (
                                ''
                            )
                        }
                        placement="top"
                        arrow
                    >
                        <HeaderTextContainer
                            bgColor={bgColor}
                            hoverBgColor={hoverBgColor}
                        >
                            <HeaderText
                                bgColor={bgColor}
                                onClick={handleVariantHeaderClick}
                                hoverBgColor={hoverBgColor}
                            >
                                {Icon}
                                {headerName}{' '}
                                {data.length ? '(' + data.length + ')' : ''}
                            </HeaderText>

                            <LabelGamificationTray
                                selectedElement={selectedElement}
                                inProgressGamificationList={
                                    inProgressGamificationList
                                }
                                alternateVariantChangeIcon={
                                    alternateVariantChangeIcon
                                }
                                isEditable={isEditable}
                            />
                        </HeaderTextContainer>
                    </AampeTooltip>
                    <HeaderContainer onClick={handleVariantHeaderClick}>
                        <HeaderText isBorderRight>Alternate</HeaderText>
                        <HeaderText>Label</HeaderText>
                    </HeaderContainer>
                    {alternateCreateError && setAlternateCreateError && (
                        <Error>
                            <div>{alternateCreateError}</div>
                            <CloseIcon
                                onClick={() => setAlternateCreateError(null)}
                            />
                        </Error>
                    )}

                    <AlternatesListWrapper
                        alternates={filteredAlternates}
                        isLoading={isLoading}
                        isEditable={isEditable}
                        listView={true}
                        isPasteButtonShow={showPasteButton}
                        showPasteAnimation={
                            variantsBulkCreateMutation.isPending
                        }
                    />
                </>
                <AddAlternateContainer isPasteButton={showPasteButton}>
                    {!showPasteButton && (
                        <AddAlternateListItem
                            formulaId={formulaId}
                            isEditable={isEditable}
                            setAlternateCreateError={setAlternateCreateError}
                            selectedElement={selectedElement}
                        />
                    )}
                    {showPasteButton && (
                        <StyledButton
                            isPrimary={false}
                            disabled={variantsBulkCreateMutation.isPending}
                            name="Paste"
                            action={handlePasteClick}
                        />
                    )}
                </AddAlternateContainer>
            </Container>
        );
    };

const HeaderContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    borderBottom: `0px`,
    cursor: 'pointer',
});
const Container = styled.div.withConfig({
    shouldForwardProp: shouldForwardProps,
})<{ isPasteButtonShow?: boolean }>(({ isPasteButtonShow }) => ({
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    backgroundColor: isPasteButtonShow ? aampeTheme.colors.backgroundWG01 : '',
    border: `1px solid ${
        isPasteButtonShow
            ? aampeTheme.colors.solidA02
            : aampeTheme.colors.interactiveWG01
    }`,
    borderTop: !isPasteButtonShow
        ? '0px'
        : `1px solid ${aampeTheme.colors.solidA02}`,
    borderBottom: !isPasteButtonShow
        ? '0px'
        : `1px solid ${aampeTheme.colors.solidA02}`,
    borderRight: !isPasteButtonShow
        ? '0px'
        : `1px solid ${aampeTheme.colors.solidA02}`,
}));
const HeaderText = styled.div.withConfig({
    shouldForwardProp: shouldForwardProps,
})<StyledProps>(({ bgColor, isBorderRight }) => ({
    display: 'flex',
    flex: isBorderRight ? 1 : 'none',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
    padding: aampeTheme.spacing4(2, 4),
    fontSize: aampeTheme.size.font.textMedium,
    lineHeight: '24px',
    fontWeight: 500,
    color: aampeTheme.colors.textAccessibleWG01,
    width: isBorderRight ? '' : '250px',
    overflow: 'hidden',
    height: '43px',
    backgroundColor: 'inherit',
    borderRight: isBorderRight
        ? `1px solid ${bgColor ?? aampeTheme.colors.interactiveWG01}`
        : '0px',
    borderBottom: '0px',
    borderLeft: '0px',
}));
const HeaderTextContainer = styled.div.withConfig({
    shouldForwardProp: shouldForwardProps,
})<StyledProps>(({ bgColor, hoverBgColor }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    backgroundColor: bgColor ?? aampeTheme.colors.backgroundWG02,
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: hoverBgColor ?? aampeTheme.colors.backgroundWG02,
    },
}));

const Error = styled.div(() => ({
    display: 'flex',
    color: aampeTheme.colors.error500,
    margin: aampeTheme.spacing4(0, 4, 4, 4),
    padding: aampeTheme.spacing4(2),
    border: `1px solid ${aampeTheme.colors.error700}`,
    backgroundColor: aampeTheme.colors.error100,
    borderRadius: aampeTheme.borderRadius.default,
}));

const CloseIcon = styled(ICONS.close)({
    display: 'flex',
    justifyContent: 'flex-end',
    marginLeft: 'auto',
    cursor: 'pointer',
});

const AddAlternateContainer = styled.div.withConfig({
    shouldForwardProp: shouldForwardProps,
})<{ isPasteButton?: boolean }>(({ isPasteButton }) => ({
    padding: isPasteButton ? aampeTheme.spacing4(1, 3) : '',
    width: '100%',
    borderTop: `1px solid ${aampeTheme.colors.interactiveWG01}`,
}));

const StyledButton = styled(BasicButton)({
    borderRadius: aampeTheme.borderRadius.default,
    fontWeight: 500,
    fontSize: aampeTheme.size.font.default,
    lineHeight: '18px',
    padding: aampeTheme.spacing4(1, 4),
    minWidth: '0px',
    height: '32px',
    color: aampeTheme.colors.solidA01,
});

const ToolTipContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    padding: aampeTheme.spacing4(3),
    fontSize: aampeTheme.size.font.default,
    lineHeight: aampeTheme.size.lineHeight.default,
    fontWeight: aampeTheme.weights.medium,
});

export default FormulaAlternateVariantDataView;
